import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import { cloneRuleSchema } from "../../schemas";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { EditAuthTags } from "../AuthTags";
import { useToast } from "../../services/utils";

interface CloneRuleDialogProps {
  cloneRuleDialog: boolean;
  handleCloseCloneRule: any;
  cloneRuleData: any;
  setCloneRuleData: React.Dispatch<React.SetStateAction<any>>;
  onSaveCloneRule: any;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CloneRuleDialog = ({
  cloneRuleDialog,
  handleCloseCloneRule,
  cloneRuleData,
  setCloneRuleData,
  onSaveCloneRule,
  setIsLoading,
}: CloneRuleDialogProps) => {
  const { showToast } = useToast();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateField = async (name: any, value: any) => {
    try {
      const partialReferenceData = { ...cloneRuleData, [name]: value };
      await cloneRuleSchema.validateAt(name, partialReferenceData);
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    } catch (validationError: any) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      await cloneRuleSchema.validate(cloneRuleData, {
        abortEarly: false,
      });
      onSaveCloneRule();
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach(
        (error: { path: string | number; message: string }) => {
          newErrors[error.path] = error.message;
        },
      );
      setErrors(newErrors);
    }
  };

  const handleClose = (reason: string) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
  };

  const handleCloseDialog = () => {
    setErrors({});
    handleCloseCloneRule();
  };

  const handleChangeCloneRule = (name: any, value: any) => {
    setCloneRuleData({
      ...cloneRuleData,
      [name]: value,
    });
    validateField(name, value);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={cloneRuleDialog}
      onClose={(event, reason) => handleClose(reason)}
      className="modal-dialog-1"
    >
      <DialogTitle className="dialog-title" component="span">
        <span>Clone Rule</span>
        <button className="close-icon" onClick={handleCloseDialog}></button>
      </DialogTitle>
      <DialogContent className="dialog-content">
        <Box>
          <Grid container rowSpacing={3} columnSpacing={3}>
            <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
              <TextField
                type="text"
                name="name"
                value={cloneRuleData?.name || ""}
                onChange={(e) =>
                  handleChangeCloneRule(e.target.name, e.target.value)
                }
                fullWidth
                variant="outlined"
                label={
                  <span>
                    Rule Name<span className="required-asterisk">*</span>
                  </span>
                }
                className={`form-control-autocomplete`}
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!errors?.name}
                helperText={errors?.name || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
              <TextField
                type="text"
                name="code"
                value={cloneRuleData?.code || ""}
                onChange={(e) =>
                  handleChangeCloneRule(e.target.name, e.target.value)
                }
                fullWidth
                variant="outlined"
                label={
                  <span>
                    Rule Code<span className="required-asterisk">*</span>
                  </span>
                }
                className={`form-control-autocomplete`}
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!errors?.code}
                helperText={errors?.code || ""}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={4}
              sx={{ display: "flex", alignItems: "flex-start" }}
            >
              <TextField
                type="text"
                name="description"
                value={cloneRuleData?.description || ""}
                onChange={(e) =>
                  handleChangeCloneRule(e.target.name, e.target.value)
                }
                label={<span>Description</span>}
                className={`form-control-autocomplete`}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
              <EditAuthTags
                selectedTagIds={cloneRuleData?.rule_tags || []}
                onTagsChange={(selectedIds) => {
                  handleChangeCloneRule("rule_tags", selectedIds);
                }}
                entityName="Rule"
                showToast={showToast}
                errors={errors?.rule_tags}
                validateField={validateField}
                fieldName="rule_tags"
                label="Tag(s)"
                required={true}
                useActiveTagsOnly={true}
                setIsLoading={setIsLoading}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions className="dialog-footer">
        <Button
          type="button"
          color="secondary"
          variant="contained"
          onClick={handleSubmit}
          className="btn-orange"
        >
          <SaveOutlinedIcon /> &nbsp; Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloneRuleDialog;
